import { useEffect, useMemo, useRef } from 'react';
import { useActiveConversation } from '../../contexts/ActiveConversationContext';
import type { ContentBlock, Role } from '../../types/api';
import { MessageBubble } from './MessageBubble';

interface BubbleEntry {
  key: string;
  role: Role;
  blocks: ContentBlock[];
  streaming: boolean;
}

export function MessageList() {
  const { messages, runState, pending } = useActiveConversation();
  const scrollerRef = useRef<HTMLDivElement>(null);

  const pendingBlocks = useMemo<ContentBlock[]>(() => {
    if (!pending) return [];
    return Object.keys(pending.blocks)
      .map(Number)
      .sort((a, b) => a - b)
      .map((i) => pending.blocks[i])
      .filter((b): b is ContentBlock => b !== undefined);
  }, [pending]);

  /** The streaming bubble is appended into the same array as `messages` and
   * keyed by `pending.localId`. The finalized assistant message in
   * ActiveConversationContext is given the same id, so React reuses the
   * bubble's DOM node across the stream→finalize transition and the msgIn
   * animation doesn't replay.
   */
  const bubbles = useMemo<BubbleEntry[]>(() => {
    const out: BubbleEntry[] = messages.map((msg) => ({
      key: msg.id,
      role: msg.role,
      blocks: msg.content,
      streaming: false,
    }));
    if (pending) {
      out.push({
        key: pending.localId,
        role: 'assistant',
        blocks: pendingBlocks,
        streaming: pendingBlocks.length === 0,
      });
    }
    return out;
  }, [messages, pending, pendingBlocks]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [bubbles, runState]);

  return (
    <div className="chat-messages" ref={scrollerRef}>
      {bubbles.map((b) => (
        <MessageBubble key={b.key} role={b.role} blocks={b.blocks} streaming={b.streaming} />
      ))}
    </div>
  );
}
